/*
* Copyright  2025-2026, Airspan
* All Rights Reserved
*
* This code is unpublished, confidential and proprietary to Airspan and may only be used in accordance with the appropriate
* license agreement. All other copying, modification, distribution, or
* reverse engineering are strictly prohibited.
*/

import React, { useEffect, useMemo, useState } from "react";
import CustomDataGrid from "../../../Shared/Table/CustomDataGrid";
import { GridColDef } from "@mui/x-data-grid-pro";
import { IThresholdCrossingSettingAction, IThresholdCrossingSettingProfile, IThresholdCrossingSettingRule, IThresholdCrossingSettingSchedule } from "../../../../store/api/admin/types";
import { Drawer } from "@mui/material";
import { Badge } from "../../../ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../ui/tabs";
import { Icon } from "../../../Shared/Icon";

interface IThresholdCrossingProfileDrawerProps {
  profile?: IThresholdCrossingSettingProfile | null;
  open: boolean;
  onClose: () => void;
}

const ThresholdCrossingProfileDrawer: React.FC<IThresholdCrossingProfileDrawerProps> = ({
  profile,
  open,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open) {
      setActiveTab(0);
    }
  }, [open]);

  const schedules = useMemo<IThresholdCrossingSettingSchedule[]>(() => profile?.schedules ?? [], [profile?.schedules]);
  const actions = useMemo<IThresholdCrossingSettingAction[]>(() => profile?.actions ?? [], [profile?.actions]);
  const rules = useMemo<IThresholdCrossingSettingRule[]>(() => profile?.rules ?? [], [profile?.rules]);

  useEffect(() => {
    if (activeTab >= schedules.length) {
      setActiveTab(0);
    }
  }, [activeTab, schedules.length]);

  const actionColumns = useMemo<GridColDef[]>(() => [
    { field: "actionType", headerName: "Action", flex: 1, sortable: false },
    {
      field: "details", headerName: "Details", flex: 1, sortable: false,
      renderCell: (cellValues: any) => {
        const values = Array.isArray(cellValues.value) ? cellValues.value : [cellValues.value];
        return (
          <div className="flex gap-1 flex-wrap">
            {values.map((val: any, index: number) => (
              <Badge key={index} className="text-xs font-semibold transition-colors" variant="secondary">
                {val}
              </Badge>
            ))}
          </div>
        );
      }
    },
  ], []);

  const ruleColumns = useMemo<GridColDef[]>(() => [
    { field: "kpi", headerName: "KPI", flex: 1, sortable: false },
    { field: "kpiType", headerName: "Type", flex: 1, sortable: false },
    {
      field: "thresholdValue",
      headerName: "Condition",
      width: 170,
      sortable: false,
      renderCell: (cellValues: any) => {
        const row = cellValues.row;
        const above = row.thresholdAbove;
        const below = row.thresholdBelow;
        if (above == null && below == null) {
          return "N/A";
        }
        if (above == null) {
          return `< ${below}`;
        }
        return `> ${above}`;
      },
    },
    { field: "consecutiveSamples", headerName: "Samples", flex: 1, sortable: false },
  ], []);

  const convertTo12Hour = (time24?: string) => {
    if (!time24) {
      return "";
    }
    const [hoursStr, minutes] = time24.split(":");
    const hours = parseInt(hoursStr, 10);
    if (Number.isNaN(hours)) {
      return time24;
    }
    const ampm = hours >= 12 ? "PM" : "AM";
    const normalized = hours % 12 || 12;
    return `${normalized.toString().padStart(2, "0")}:${minutes} ${ampm}`;
  };

  const scheduleDetails = schedules[activeTab];
  const scheduleDays = scheduleDetails?.days?.length ? scheduleDetails.days.join(", ") : "";

  const classifyDays = (days: string[]) => {
    const weekdays = ["MON", "TUE", "WED", "THU", "FRI"];
    const weekends = ["SAT", "SUN"];
    if (days.length === 7 && weekdays.every(d => days.includes(d)) && weekends.every(d => days.includes(d))) {
      return "Every day";
    }
    if (days.every(d => weekdays.includes(d))) {
      return "Weekdays";
    }
    if (days.every(d => weekends.includes(d))) {
      return "Weekends";
    }
    return "";
  };

  const isEnabled = Boolean(profile?.enabled);

  return (
    <Drawer
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: {
          width: "560px",
          maxWidth: "90vw",
          height: "100%",
          top: 0,
          backgroundColor: "var(--color-background)",
          borderLeft: "1px solid var(--color-border)",
          borderRadius: "10px 0 0 10px",
          boxShadow: "-4px 0 24px 0 rgba(0,0,0,0.08)",
          overflow: "hidden",
        },
      }}
      ModalProps={{ disableEnforceFocus: true }}
      anchor="right"
      open={open}
      onClose={onClose}
    >
      <div className="flex flex-col h-full min-h-0 bg-background">
        {/* ─── Header ─── */}
        <div className="relative pr-12 shrink-0 px-4 pt-2 pb-2">
          <button
            type="button"
            className="absolute right-2 top-2 h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-md bg-transparent hover:bg-transparent text-foreground border-0 p-0 focus-visible:outline-none"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon name="close" size={20} />
          </button>

          <div className="flex items-center gap-2 pr-8">
            <h2 className="text-xl font-semibold truncate text-foreground" title={profile?.name ?? ""}>
              {profile?.name ?? ""}
            </h2>
            <Badge variant={isEnabled ? "default" : "secondary"} className={isEnabled ? "bg-primary text-primary-foreground" : ""}>
              {isEnabled ? "Enabled" : "Disabled"}
            </Badge>
          </div>
        </div>

        {/* ─── Scrollable content ─── */}
        <div className="flex-1 min-h-0 overflow-auto px-4 pb-4 space-y-4">
          {/* Description */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-1">Description</h4>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {profile?.description ?? "—"}
            </p>
          </div>

          <div data-orientation="horizontal" role="none" className="shrink-0 bg-border h-[1px] w-full"></div>
          {/* Actions */}
          <div className="mb-4 space-y-3">
            <h4 className="text-sm font-semibold text-foreground">Actions</h4>
            {actions.length > 0 ? (
              <CustomDataGrid
                tableId="thresholdProfileActionReadOnly"
                rows={actions}
                columns={actionColumns}
                checkboxSelection={false}
                rowCount={actions.length}
                loading={false}
                disableColumnMenu
                disableRowSelectionOnClick
                getRowId={(row: IThresholdCrossingSettingAction) => row.id}
                isRowSelectable={() => false}
                hideFooter
                sx={{
                  height: "110px",
                  backgroundColor: "transparent",
                  "& .MuiDataGrid-columnHeaders": { backgroundColor: "transparent", borderBottom: "1px solid var(--color-border)" },
                  "& .MuiDataGrid-virtualScroller": { backgroundColor: "transparent" },
                  "& .MuiDataGrid-footerContainer": { backgroundColor: "transparent" },
                  "& .MuiDataGrid-overlay": { backgroundColor: "transparent" },
                }}
                fillContainer
              />
            ) : (
              <p className="text-sm text-muted-foreground">No actions found.</p>
            )}
          </div>
          <div data-orientation="horizontal" role="none" className="shrink-0 bg-border h-[1px] w-full"></div>
          {/* Schedule */}
          <div className="mb-4 space-y-3">
            <h4 className="text-sm font-semibold text-foreground">Schedule</h4>
            {schedules.length > 0 ? (
              <Tabs value={String(activeTab)} onValueChange={(val) => setActiveTab(Number(val))} className="w-full">
                <div className="flex items-center justify-between">
                  <TabsList className="bg-muted text-muted-foreground rounded-[4px] px-1 py-1 h-auto gap-0.5">
                    {schedules.map((record, index) => (
                      <TabsTrigger
                        key={record.id}
                        value={String(index)}
                        className="rounded-lg px-4 py-1.5 text-[0.875rem] font-extralight border-0 bg-muted text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                      >
                        {index + 1}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>

                {schedules.map((record, index) => (
                  <TabsContent
                    key={record.id}
                    value={String(index)}
                    className="mt-3"
                  >
                    <div className="rounded-lg border border-border p-4 shadow-sm bg-card">
                      <div className="flex flex-col gap-3 text-foreground">
                        {/* Calendar + Days */}
                        <div className="flex items-center gap-2 text-sm">
                          <Icon name="calendar_today" size={18} />
                          <div>
                            <span className="font-semibold">{classifyDays(record.days)}</span>{" "}
                            <span className="font-medium text-muted-foreground pl-1">{record.days.join(", ")}</span>
                          </div>
                        </div>
                        {/* Clock + Time */}
                        <div className="flex items-center gap-2 text-sm">
                          <Icon name="schedule" size={16} className="text-muted-foreground" />
                          <span className="font-medium">{convertTo12Hour(record.fromTime)} – {convertTo12Hour(record.toTime)}</span>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            ) : (
              <p className="text-sm text-muted-foreground">No schedules found.</p>
            )}
          </div>
          <div data-orientation="horizontal" role="none" className="shrink-0 bg-border h-[1px] w-full"></div>
          {/* Rules */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-foreground">Alert when...</h4>
            {rules.length > 0 ? (
              <CustomDataGrid
                tableId="thresholdProfileRuleReadOnly"
                rows={rules}
                columns={ruleColumns}
                checkboxSelection={false}
                rowCount={rules.length}
                loading={false}
                disableColumnMenu
                disableRowSelectionOnClick
                getRowId={(row: IThresholdCrossingSettingRule) => row.id}
                isRowSelectable={() => false}
                hideFooter
                sx={{
                  maxHeight: "320px",
                  backgroundColor: "transparent",
                  "& .MuiDataGrid-columnHeaders": { backgroundColor: "transparent", borderBottom: "1px solid var(--color-border)" },
                  "& .MuiDataGrid-virtualScroller": { backgroundColor: "transparent" },
                  "& .MuiDataGrid-footerContainer": { backgroundColor: "transparent" },
                  "& .MuiDataGrid-overlay": { backgroundColor: "transparent" },
                }}
                fillContainer
              />
            ) : (
              <p className="text-sm text-muted-foreground">No rules found.</p>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default ThresholdCrossingProfileDrawer;
